/// <amd-module name="VSS/Features/MasterDetails" />
import { IObservableValue } from "VSS/Core/Observable";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext } from "VSS/Platform/Context";
import { IMasterDetailsContext, IMasterDetailsContextLayer } from "VSSUI/MasterDetailsContext";

/**
 * Creates a layer that is aware of a history key that identifies it has a selected item
 * When pushed, it will automatically update the selectedMasterItem observable to match what it can find in
 * When the selected item changes, updates the URL
 * @param layer The base layer to copy and attach new handlers to
 */
export function createHistoryAwareLayer<P>(
    layer: IMasterDetailsContextLayer<string, P>,
    pageContext: IVssPageContext
): IMasterDetailsContextLayer<string, P>;

/**
 * Creates a layer that is aware of a history key that identifies it has a selected item
 * When pushed, it will automatically update the selectedMasterItem observable to match what it can find in
 * When the selected item changes, updates the URL
 * @param layer The base layer to copy and attach new handlers to
 * @param selectedItemToString Maps a given selected item to a string to place in the URL
 * @param stringToSelectedItem Lookup function to find an item from the URL param
 */
export function createHistoryAwareLayer<T, P>(
    layer: IMasterDetailsContextLayer<T, P>,
    pageContext: IVssPageContext,
    selectedItemToString: (item: T) => string,
    stringToSelectedItem: (itemAsString: string) => T
): IMasterDetailsContextLayer<T, P>;

export function createHistoryAwareLayer<T, P>(
    layer: IMasterDetailsContextLayer<T, P>,
    pageContext: IVssPageContext,
    selectedItemToString?: (item: T) => string,
    stringToSelectedItem?: (itemAsString: string) => T
): IMasterDetailsContextLayer<T, P> {
    const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

    const newOnPush = (context: IMasterDetailsContext) => {
        layer.onPushed && layer.onPushed(context);

        if (selectedItemToString && stringToSelectedItem) {
            pageStateHistoryService.registerObservable(
                layer.selectedMasterItem,
                layer.key,
                HistoryBehavior.replace,
                selectedItemToString,
                stringToSelectedItem
            );
        } else {
            pageStateHistoryService.registerObservable(
                (layer.selectedMasterItem as unknown) as IObservableValue<string>,
                layer.key,
                HistoryBehavior.replace
            );
        }
    };

    const newOnPop = (context: IMasterDetailsContext) => {
        layer.onPopped && layer.onPopped(context);

        pageStateHistoryService.unregisterObservable(layer.key);
        pageStateHistoryService.queueValueChange(layer.key, "", HistoryBehavior.replace);
    };

    return { ...layer, onPushed: newOnPush, onPopped: newOnPop };
}

/**
 * Creates a new layer based on the one passed in that is aware that is has a child layer that cares about URL params
 * This new layer will automatically create and push the child layer if it detects the child layer's key in history state
 * when it is pushed into the context
 * @param layer The template layer
 * @param childLayerKey The key that will be stamped on the childLayer - used for history lookup
 * @param childLayerFactory Used to create the child layer; optionally returns undefined if there's a data mismatch etc.
 */
export function createChildAwareLayer<T, P>(
    layer: IMasterDetailsContextLayer<T, P>,
    childLayerKey: string,
    childLayerFactory: (selectedHistoryItem: string) => IMasterDetailsContextLayer<any, any> | undefined,
    pageContext: IVssPageContext
) {
    const pageStateHistoryService = pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

    const newOnPush = (context: IMasterDetailsContext) => {
        layer.onPushed && layer.onPushed(context);

        const historyState = pageStateHistoryService.getProjectedState();

        if (historyState[childLayerKey]) {
            const childLayer = childLayerFactory(historyState[childLayerKey]);
            if (childLayer) {
                context.push(childLayer);
            } else {
                // If we didn't find something, clear it out from the URL so it doesn't cause an issue later
                pageStateHistoryService.queueValueChange(childLayerKey, "", HistoryBehavior.replace);
            }
        }
    };

    return { ...layer, onPushed: newOnPush };
}
